import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActionLink() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: "loading" });
  const [delayForm, setDelayForm] = useState(false);
  const [delayDate, setDelayDate] = useState("");
  const [delayReason, setDelayReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/action/${token}`)
      .then((r) => setState({ phase: "ready", info: r.data }))
      .catch((e) => setState({ phase: "error", message: e?.response?.data?.detail || "This link is invalid or has expired." }));
  }, [token]);

  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/action/${token}`, { action, comment: comment || undefined, ...extra });
      setState({ phase: "done", changes: r.data.changes, po_number: r.data.po_number });
    } catch (e) {
      setState({ phase: "error", message: e?.response?.data?.detail || "Action failed." });
    } finally { setBusy(false); }
  };

  const { phase } = state;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-md" data-testid="action-link-page">
        <div className="mb-4 flex items-center justify-center gap-2 text-slate-600">
          <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-6 w-auto rounded bg-white px-1.5 py-0.5" /><span className="font-heading text-sm font-semibold">· Secure Supplier Action</span>
        </div>

        {phase === "loading" && (
          <div className="rounded-xl border bg-white p-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>
        )}

        {phase === "error" && (
          <div className="rounded-xl border bg-white p-8 text-center" data-testid="action-error">
            <AlertTriangle className="mx-auto h-8 w-8 text-[#DC2626]" />
            <p className="mt-3 text-sm font-medium">{state.message}</p>
          </div>
        )}

        {phase === "done" && (
          <div className="rounded-xl border bg-white p-8 text-center" data-testid="action-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#22C55E]" />
            <h1 className="mt-3 font-heading text-xl font-semibold">Update received</h1>
            <p className="mt-1 text-sm text-muted-foreground">{state.po_number} — the buyer's dashboard has been updated in real time.</p>
            {state.changes?.length > 0 && (
              <div className="mt-4 space-y-1.5 text-left">
                {state.changes.map((c, i) => (
                  <p key={i} className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800"><CheckCircle2 className="h-3.5 w-3.5" />{c}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {phase === "ready" && (
          <div className="rounded-xl border bg-white">
            <div className="border-b p-5">
              <p className="text-xs text-muted-foreground">{state.info.company} → {state.info.supplier_name}</p>
              <h1 className="mt-1 font-mono font-heading text-xl font-semibold" data-testid="action-po-number">{state.info.po.po_number}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{state.info.po.item} · Qty {state.info.po.quantity}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md bg-slate-50 p-2"><p className="text-muted-foreground">Current status</p><p className="font-semibold">{state.info.po.status_label}</p></div>
                <div className="rounded-md bg-slate-50 p-2"><p className="text-muted-foreground">Expected delivery</p><p className="font-semibold">{state.info.po.expected_delivery_date || state.info.po.required_delivery_date}</p></div>
              </div>
            </div>
            <div className="space-y-3 p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Update this PO — one tap, no login needed</p>
              <div className="grid grid-cols-2 gap-2" data-testid="action-buttons">
                {state.info.actions.filter((a) => a.key !== "delayed").map((a) => (
                  <Button key={a.key} data-testid={`action-${a.key}`} disabled={busy}
                    variant={a.key === "acknowledge" ? "default" : "outline"}
                    className={a.key === "acknowledge" ? "bg-slate-900 hover:bg-slate-800" : ""}
                    onClick={() => act(a.key)}>
                    {a.label}
                  </Button>
                ))}
              </div>
              {!delayForm ? (
                <Button variant="outline" data-testid="action-delayed" disabled={busy} className="w-full border-red-200 text-red-700 hover:bg-red-50" onClick={() => setDelayForm(true)}>
                  <AlertTriangle className="mr-1.5 h-4 w-4" /> Report a Delay
                </Button>
              ) : (
                <div className="space-y-2 rounded-lg border border-red-200 bg-red-50/50 p-3" data-testid="delay-form">
                  <p className="text-xs font-semibold text-red-800">Report delay</p>
                  <Input data-testid="delay-date" type="date" value={delayDate} onChange={(e) => setDelayDate(e.target.value)} className="bg-white" />
                  <Input data-testid="delay-reason" placeholder="Reason (e.g. raw material shortage)" value={delayReason} onChange={(e) => setDelayReason(e.target.value)} className="bg-white" />
                  <Button data-testid="delay-submit" disabled={busy} onClick={() => act("delayed", { expected_delivery_date: delayDate || undefined, reason: delayReason || undefined })} className="w-full bg-red-600 hover:bg-red-700">Submit Delay Report</Button>
                </div>
              )}
              <Textarea data-testid="action-comment" placeholder="Optional comment for the buyer…" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
              <p className="text-center text-[10px] text-slate-400">Secure expiring link · expires {new Date(state.info.expires_at).toLocaleString("en-IN")} · Every action is recorded on the PO audit trail</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
